import { ApiServerUrl } from 'redux/config/services/Constants'
import { EmployerApiKey, RevocationCadence } from './apiRosterSyncTypes'

export const CADENCE_OPTIONS: {
  value: RevocationCadence
  label: string
  description: string
}[] = [
  {
    value: '90',
    label: '90 days',
    description: 'Automatically expire keys after 90 days',
  },
  {
    value: '180',
    label: '180 days',
    description: 'Automatically expire keys after 180 days',
  },
  {
    value: '365',
    label: '365 days',
    description: 'Automatically expire keys after 365 days',
  },
  {
    value: 'never',
    label: 'Never (manual only)',
    description: 'Keys stay valid until you revoke them',
  },
]

export const formatKeyValidity = (
  expiresAt: string | null | undefined
): string => {
  if (!expiresAt) return 'Valid until you revoke it'
  const expires = new Date(expiresAt)
  if (Number.isNaN(expires.getTime())) return 'Valid until you revoke it'
  const days = Math.ceil((expires.getTime() - Date.now()) / 86400000)
  const dateLabel = expires.toLocaleDateString()
  if (days <= 0) return `Expired ${dateLabel}`
  return `Valid ${days} day${days === 1 ? '' : 's'}, expires ${dateLabel}`
}

export const formatRevealValidity = (
  expiresAt: string | null | undefined
): string => {
  if (!expiresAt) {
    return 'This key stays valid until you revoke it. It will not expire on its own.'
  }
  const expires = new Date(expiresAt)
  if (Number.isNaN(expires.getTime())) {
    return 'This key stays valid until you revoke it. It will not expire on its own.'
  }
  const days = Math.ceil((expires.getTime() - Date.now()) / 86400000)
  const dateLabel = expires.toLocaleDateString()
  if (days <= 0) return `This key expired on ${dateLabel}.`
  return `This key expires on ${dateLabel} (${days} day${
    days === 1 ? '' : 's'
  }). We email you 14 days before so you can generate an overlapping key.`
}

export const isActiveUnexpiredKey = (key: EmployerApiKey): boolean =>
  !key.revokedAt && !key.isExpired

export const EMPLOYER_CSV_UPLOAD_PATH = '/v1/employer-csv-upload'

export const getEmployerCsvUploadUrl = (): string =>
  `${ApiServerUrl}${EMPLOYER_CSV_UPLOAD_PATH}`

const FALLBACK_API_ERROR = 'Please try again or contact support.'

const asMessageList = (value: unknown): string[] => {
  if (typeof value === 'string' && value.trim()) return [value.trim()]
  if (Array.isArray(value)) {
    return value.flatMap(asMessageList)
  }
  return []
}

/** User-facing text from a DRF/axios error (`detail` or field errors). */
export const getApiErrorDetail = (error: unknown): string => {
  if (!error || typeof error !== 'object') return FALLBACK_API_ERROR
  const data = (error as { response?: { data?: unknown } }).response?.data
  if (typeof data === 'string' && data.trim()) return data.trim()
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>
    const fromDetail = asMessageList(record.detail)
    if (fromDetail.length) return fromDetail.join(' ')
    const fromFields = Object.entries(record)
      .filter(([key]) => key !== 'status' && key !== 'statusText')
      .flatMap(([, value]) => asMessageList(value))
    if (fromFields.length) return fromFields.join(' ')
  }
  if (error instanceof Error && error.message.trim()) return error.message
  return FALLBACK_API_ERROR
}
